import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgendaDiaResumo } from '../../../../interfaces/agenda-dia';
import { AgendaFilaService } from '../../../../services/agenda-fila/agenda-fila.service';
import { AgendaFiltroEntrada } from '../agenda-filtro';

export interface AgendasDoDiaBloco {
  titulo: string;
  agendas: AgendaDiaResumo[];
  /** Sem agendamento fica sem total: as vagas dela não entram na conta do dia. */
  comTotal: boolean;
  vagas: number;
  agendadas: number;
  livres: number;
}

/**
 * Visão "Agendas do Dia" do painel da Agenda: que agendas funcionam no dia na unidade, com vagas,
 * agendadas e livres (mesma contagem do relatório 896 do sim-relatorios). Unidade e data vêm do painel.
 */
@Component({
  selector: 'app-agendas-do-dia',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agendas-do-dia.component.html',
  styleUrl: './agendas-do-dia.component.scss',
})
export class AgendasDoDiaComponent implements OnChanges {
  @Input() cdEstabelecimento: number | null = null;
  /** yyyy-MM-dd */
  @Input() data = '';
  @Input() nomeUnidade = '';
  @Output() abrirAgenda = new EventEmitter<AgendaFiltroEntrada>();

  busca = '';
  agendas: AgendaDiaResumo[] = [];
  carregando = false;
  erro = '';
  private sequencia = 0;

  constructor(private agendaFilaService: AgendaFilaService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cdEstabelecimento'] || changes['data']) void this.carregar();
  }

  async carregar(): Promise<void> {
    const minha = ++this.sequencia;
    if (!this.cdEstabelecimento || !this.data) {
      this.agendas = [];
      this.carregando = false;
      return;
    }
    this.carregando = true;
    this.erro = '';
    try {
      const agendas = await this.agendaFilaService.getAgendasDiaResumo(this.cdEstabelecimento, this.data);
      // Resposta de uma troca de dia/unidade anterior não sobrescreve a atual.
      if (minha === this.sequencia) this.agendas = agendas ?? [];
    } catch {
      if (minha === this.sequencia) {
        this.agendas = [];
        this.erro = 'Não foi possível carregar as agendas do dia.';
      }
    } finally {
      if (minha === this.sequencia) this.carregando = false;
    }
  }

  private get filtradas(): AgendaDiaResumo[] {
    const termo = this.busca.trim().toLowerCase();
    if (!termo) return this.agendas;
    return this.agendas.filter(a => `${a.nmAgenda} ${a.dsEspecialidade}`.toLowerCase().includes(termo));
  }

  get quantidade(): number {
    return this.filtradas.length;
  }

  /** Os três blocos do relatório, sempre na mesma ordem, mesmo vazios. */
  get blocos(): AgendasDoDiaBloco[] {
    const agendas = this.filtradas;
    const grupos: [string, AgendaDiaResumo[], boolean][] = [
      ['Presencial', agendas.filter(a => !a.online && a.agendadas > 0), true],
      ['Online', agendas.filter(a => a.online), true],
      ['Sem agendamento', agendas.filter(a => !a.online && a.agendadas === 0), false],
    ];
    return grupos.map(([titulo, lista, comTotal]) => ({
      titulo,
      agendas: lista,
      comTotal,
      vagas: lista.reduce((s, a) => s + a.vagas, 0),
      agendadas: lista.reduce((s, a) => s + a.agendadas, 0),
      livres: lista.reduce((s, a) => s + a.livres, 0),
    }));
  }

  ocupacao(a: { vagas: number; agendadas: number }): number {
    return a.vagas ? Math.round((a.agendadas / a.vagas) * 100) : 0;
  }

  rotuloTipo(tipo: string): string {
    return tipo === 'EXAME' ? 'Exame' : tipo === 'ODONTO' ? 'Odonto' : 'Consulta';
  }

  /** Abre a grade nesta agenda e data; agenda de odonto abre no tipo Odontologia, como no original. */
  abrirNaAgenda(a: AgendaDiaResumo): void {
    if (!this.cdEstabelecimento) return;
    this.abrirAgenda.emit({
      tipo: a.tipo === 'EXAME' ? 'EXAME' : a.tipo === 'ODONTO' ? 'ODONTOLOGIA' : 'CONSULTA',
      cdEstabelecimento: this.cdEstabelecimento,
      cdAgenda: a.cdAgenda,
      data: this.data,
      nmAgenda: a.nmAgenda,
    });
  }
}
